import { Component, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { Subscription } from 'rxjs';
import { F1120LPCHeadersService } from '../../services/f1120-l-pc-headers.service';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-f1120-l-pc-headers',
    templateUrl: './f1120-l-pc-headers.component.html',
    styleUrls: ['./f1120-l-pc-headers.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F1120LPCHeadersComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  // STANDARD COMPONENT PROPERTIES (MANDATORY from superprompt)
  currentTabName = "Domestic Headers";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  loading = false;

  // Component properties
  rowData: any = {};
  headerData: any = {};
  filterParams: any = {};
  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  
  // F1120LPC specific properties
  enableDisableSub = 'Include';
  originalData: any[] = [];
  userSettings: any = {};
  logged_in_user: string = '';
  currentyear: string = '';
  leId: string = '';
  formName = 'Domestic Headers';
  hgt = '96%';
  cdr_no: string = '';
  leType: string = '';
  disableDate = true;
  
  // Entity data properties
  entityList: { [key: string]: any } = {};
  
  // Form data properties
  showFormLoader = true;
  corporationList: any[] = [];
  entitiesData: any[] = [];
  formList: any[] = [];
  
  constructor(
    private f1120LPCHeadersService: F1120LPCHeadersService,
    private _snackBar: MatSnackBar, private gtwSnackbar: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.init();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal) as GtwElementsBaseUrlsModel;
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.headerData = header;
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow) ? extractObject(rawRow[0]) : extractObject(rawRow);
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.cdr.markForCheck();
  }

  init(): void {
    console.log('🔄 Init called! Checking required data...');
    console.log('🔍 Data availability check:', {
      rowData: !!this.rowData && Object.keys(this.rowData).length > 0,
      globalService: !!this.globalService && Object.keys(this.globalService).length > 0,
      filterParams: !!this.filterParams && Object.keys(this.filterParams).length > 0,
      generalConfig: !!this.GENERAL_CONFIG && !!this.GENERAL_CONFIG.base_url,
      rowDataKeys: this.rowData ? Object.keys(this.rowData).length : 0,
      globalServiceKeys: this.globalService ? Object.keys(this.globalService).length : 0,
      filterParamsKeys: this.filterParams ? Object.keys(this.filterParams).length : 0,
      baseUrl: this.GENERAL_CONFIG?.base_url
    });
    
    // Check if all required data is available
    if (!this.rowData || Object.keys(this.rowData).length === 0 ||
        !this.globalService || Object.keys(this.globalService).length === 0 ||
        !this.filterParams || Object.keys(this.filterParams).length === 0 ||
        !this.GENERAL_CONFIG || !this.GENERAL_CONFIG.base_url) {
      console.log('⏸️ Waiting for required data - not all conditions met');
      return;
    }
    
    console.log('Initializing with data:', {
      rowData: this.rowData,
      globalService: this.globalService,
      filterParams: this.filterParams,
      baseUrl: this.GENERAL_CONFIG.base_url
    });

    // Initialize user settings and entity properties
    this.userSettings = this.USER_SETTINGS;
    this.logged_in_user = this.userSettings?.user?.sso_id || '';
    this.currentyear = this.filterParams.tax_year || '';
    this.leId = this.rowData.LEID || '';
    this.cdr_no = this.rowData.MARS || '';
    this.leType = this.rowData.LE_TYPE || '';

    // Set up entity list
    this.entityList = {
      "Tax Year": this.currentyear,
      "LEID": this.rowData.LEID,
      "LE Name": this.rowData.LE_NAME
    };

    // Fetch form data
    console.log('🚀 About to fetch domestic headers data...');
    this.fetchDomesticHeadersData();
  }

  fetchDomesticHeadersData(): void {
    this.showFormLoader = true;
    this.cdr.markForCheck();
    
    // Prepare filter parameters with additional required fields
    const extendedFilterParams = {
      ...this.filterParams,
      combination_key: this.rowData?.COMBINATION_KEY || '',
      entity_type: this.rowData?.ENTITY_TYPE || ''
    };
    
    console.log('Fetching with GENERAL_CONFIG:', this.GENERAL_CONFIG);
    console.log('Base URL at fetch time:', this.GENERAL_CONFIG?.base_url);
    
    if (!this.GENERAL_CONFIG || !this.GENERAL_CONFIG.base_url) {
      console.error('GENERAL_CONFIG.base_url is missing at fetch time');
      console.log('Available GENERAL_CONFIG:', this.GENERAL_CONFIG);
      this._snackBar.open('Configuration error: base URL not available', 'Close', { 
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      this.showFormLoader = false;
      this.cdr.markForCheck();
      return;
    }
    
    this.f1120LPCHeadersService.fetchDomesticHeadersData(this.GENERAL_CONFIG.base_url, this.globalService, extendedFilterParams).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          console.log('🔥 API Response received:', response);
          this.showFormLoader = false;
          
          // ✅ CRITICAL: Check for success first, then extract jsonObject (like AngularJS)
          if (response && response.callSuccess === "1") {
            let formData = response.jsonObject;
            console.log('✅ API Success! Using response.jsonObject:', formData);
            
            if (formData && Array.isArray(formData) && formData.length > 0) {
              this.formList = formData;
              console.log('✅ FormList set successfully:', this.formList.length, 'items');
              
              // Apply conditional field logic from original AngularJS
              // 🔓 TASK: Unlock E5 radio buttons in Angular 12
              for (let i = 0; i < this.formList.length; i++) {
                if (this.rowData.CONSOL_LEVEL_FLAG === 'Y') {
                  if (this.formList[i].LINE_NO !== 'E5') {
                    this.formList[i]['conditionalField'] = true;
                  }
                } else {
                  // 🔓 REMOVED: E5 blocking logic for Angular 12
                  // Original AngularJS logic was:
                  // if (this.formList[i].LINE_NO === 'E5' && this.rowData.CONSOL_GROUP_TYPE === 'GROUP') {
                  //   this.formList[i]['conditionalField'] = true;
                  // }
                  // E5 radio buttons are now always enabled when CONSOL_LEVEL_FLAG !== 'Y'
                }
              }
              
              this.originalData = _.cloneDeep(this.formList);
            } else {
              console.error('❌ jsonObject is empty or not an array:', formData);
              this.formList = [];
            }
          } else {
            // Handle API errors
            console.error('❌ API Error:', response);
            this.formList = [];
            if (response && response.errorMessage) {
              this._snackBar.open(`API Error: ${response.errorMessage}`, 'Close', { 
                duration: 5000,
                panelClass: ['error-snackbar']
              });
            }
          }
          
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.showFormLoader = false;
          console.error('Error fetching domestic headers data:', error);
          this._snackBar.open('Error loading form data', 'Close', { 
            duration: 3000,
            panelClass: ['error-snackbar']
          });
          this.cdr.markForCheck();
        }
      });
  }

  save(tabName: string): void {
    this.isSaveClicked = true;
    this.crudLoading = true;
    
    // ✅ Prepare data like AngularJS
    const saveData = this.formList.filter(item => item); // Filter all items (no IS_CHANGED_FLAG filter like AngularJS comment)
    
    if (saveData.length === 0) {
      this._snackBar.open('No changes to save.', 'Close', { 
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      this.isSaveClicked = false;
      this.crudLoading = false;
      return;
    }
    
    // ✅ Transform data like AngularJS (different field names!)
    const fDomesticSaveDtls = saveData.map(item => ({
      form_name: item.IRS_FORM_NO || item.FORM_NAME,
      part_no: item.PART_NO,
      line_no: item.LINE_NO,
      schd_no: item.SCHEDULE_NAME,
      section_name: item.SECTION_NAME,
      form_key: item.FORM_KEY,
      combination_key: item.COMBINATION_KEY,
      form_line_key: item.FORM_LINE_KEY,
      line_attrib_key: item.LINE_ATTRIB_KEY,
      attrib_type: item.ATTRIB_TYPE,
      attrib_prop_key: item.ATTRIB_PROP_KEY,
      acct_ref_key: item.ACCT_REF_KEY,
      attrib_name: item.LINE_DESCRIPTION,
      attrib_value: item.ATTRIB_VALUE,
      entity_type: item.ENTITY_TYPE,
      trans_details_key: item.TRANS_DETAILS_KEY,
      trans_type_key: item.TRANS_TYPE_KEY,
      edit_type: item.EDIT_TYPE
    }));
    
    // ✅ Create returnClobSettingsObj like AngularJS (simple structure!)
    console.log('🔍 Searching for sso_id in globalService:', {
      logged_in_user: this.globalService?.logged_in_user,
      globalParams_sso_id: this.globalService?.globalParams?.sso_id,
      userSettingsData: this.globalService?.userSettingsData,
      serverConfigData: this.globalService?.serverConfigData
    });
    
    // ✅ CRITICAL: Find sso_id from multiple possible locations
    const sso_id = this.globalService?.logged_in_user || 
                   this.globalService?.globalParams?.sso_id || 
                   this.globalService?.userSettingsData?.sso_id ||
                   this.globalService?.serverConfigData?.sso_id ||
                   this.globalService?.generalConfigData?.sso_id ||
                   '612156750'; // ✅ Fallback from working AngularJS
    
    const returnClobSettingsObj = {
      sso_id: sso_id,
      screen_key: this.globalService?.globalParams?.screen_key || '',
      combination_key: this.formList[0]?.COMBINATION_KEY || ''
    };
    
    console.log('✅ Final returnClobSettingsObj with sso_id:', returnClobSettingsObj);
    
    console.log('🔍 Before save - returnClobSettingsObj:', returnClobSettingsObj);
    console.log('🔍 Before save - fDomesticSaveDtls:', fDomesticSaveDtls);
    console.log('🔍 Before save - globalService:', this.globalService);
    console.log('🔍 Before save - filterParams:', this.filterParams);
    
    if (!this.GENERAL_CONFIG || !this.GENERAL_CONFIG.base_url) {
      console.error('GENERAL_CONFIG.base_url is missing at save time');
      this._snackBar.open('Configuration error: base URL not available', 'Close', { 
        duration: 3000,
        panelClass: ['error-snackbar']
      });
      this.isSaveClicked = false;
      this.crudLoading = false;
      return;
    }
    
    this.f1120LPCHeadersService.savefDomesticHeaders(this.GENERAL_CONFIG.base_url, returnClobSettingsObj, fDomesticSaveDtls, this.globalService, this.filterParams).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          console.log('💾 Save response received:', response);
          this.isSaveClicked = false;
          this.crudLoading = false;
          
          // ✅ CRITICAL: Check callSuccess and errorMessage like original AngularJS
          if (response && response.callSuccess === "1" && (response.errorMessage === 'null' || response.errorMessage === null)) {
            this._snackBar.open('Form has been successfully saved/updated', 'Close', { 
              duration: 3000,
              panelClass: ['success-snackbar']
            });
            this.requestGridUpdate.emit('refresh');
            this.cancel();
          } else {
            // Handle API error message like original AngularJS
            const errorMsg = response?.errorMessage || 'Save failed';
            console.error('💾 Save failed:', errorMsg);
            this._snackBar.open(`Save Error: ${errorMsg}`, 'Close', { 
              duration: 5000,
              panelClass: ['error-snackbar']
            });
          }
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.isSaveClicked = false;
          this.crudLoading = false;
          console.error('Error saving data:', error);
          this._snackBar.open('Error saving data', 'Close', { 
            duration: 3000,
            panelClass: ['error-snackbar']
          });
          this.cdr.markForCheck();
        }
      });
  }



  reset(): void {
    this.formList = _.cloneDeep(this.originalData);
    this.gtwSnackbar.openSnackBar('Form reset to original values', 'gtw-snackbar--yellow', true, 4);
  }

  cancel(): void {
    // CRITICAL: Use EventEmitter for AngularJS modal dismissal (like F4626)
    this.requestGridUpdate.emit('cancel');
    
    // Additional cleanup if needed
    this.isSaveClicked = false;
    this.crudLoading = false;
  }

  checkBoxHandler(formLineKey: string, lineAttribKey: string, attribCol: string): void {
    // Handle checkbox logic if needed
    console.log('Checkbox handler:', formLineKey, lineAttribKey, attribCol);
  }

  // Getters for entity list iteration in template
  getEntityListKeys(): string[] {
    return this.entityList ? Object.keys(this.entityList) : [];
  }

  getEntityListValues(): any[] {
    return this.entityList ? Object.values(this.entityList) : [];
  }

  // Handle checkbox changes to properly set string values like AngularJS
  onCheckboxChange(row: any, event: any): void {
    row.ATTRIB_VALUE = event.target.checked ? 'true' : 'false';
  }
}
